import { useState, useEffect } from 'react';
import { Plus, Play, Pause, CheckCircle, Warning, X } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useAriaContext } from '../contexts/AriaContext';
import SmartPicker from '../components/SmartPicker';
import WorkflowActions from '../components/WorkflowActions';
import DraftRestoreBanner from '../components/DraftRestoreBanner';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import { useHighlight } from '../hooks/useHighlight';

const STATUS_CONFIG = {
  planned: { label: 'PLANNED', color: 'hsl(0,0%,50%)', bg: 'hsl(0,0%,8%)', border: 'hsl(0,0%,20%)' },
  in_progress: { label: 'IN PROGRESS', color: 'hsl(200,80%,55%)', bg: 'hsl(200,80%,8%)', border: 'hsl(200,80%,25%)' },
  qc_hold: { label: 'QC HOLD', color: 'hsl(12,90%,55%)', bg: 'hsl(12,90%,8%)', border: 'hsl(12,90%,30%)' },
  completed: { label: 'COMPLETED', color: 'hsl(197,100%,48%)', bg: 'hsl(197,100%,5%)', border: 'hsl(197,100%,15%)' },
};

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

function WOCard({ wo, onStatusChange, highlightId, userRole }) {
  const cfg = STATUS_CONFIG[wo.status] || STATUS_CONFIG.planned;
  const progress = wo.planned_qty > 0 ? ((wo.completed_qty || 0) / wo.planned_qty * 100).toFixed(0) : 0;
  const daysLeft = wo.planned_end ? Math.ceil((new Date(wo.planned_end) - new Date()) / 86400000) : null;
  const isHighlighted = highlightId && (wo.id === highlightId || wo.wo_no === highlightId);

  return (
    <div data-testid={`wo-card-${wo.wo_no}`}
      style={{
        background: cfg.bg, border: `1px solid ${cfg.border}`, padding: 14, marginBottom: 8,
        ...(isHighlighted ? { boxShadow: '0 0 0 1px hsl(75,100%,50%)' } : {}),
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: cfg.color,
            fontWeight: 700, marginBottom: 2 }}>{wo.wo_no}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,70%)',
            lineHeight: 1.3 }}>{wo.product_name}</div>
        </div>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: cfg.color,
          background: cfg.bg, border: `1px solid ${cfg.border}`, padding: '2px 6px',
          textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {cfg.label}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase' }}>Planned Qty</div>
          <div style={{ fontSize: 12, color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600 }}>{wo.planned_qty?.toLocaleString()}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase' }}>Completed</div>
          <div style={{ fontSize: 12, color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600 }}>{(wo.completed_qty || 0).toLocaleString()}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase' }}>Line / Operator</div>
          <div style={{ fontSize: 10, color: 'hsl(0,0%,65%)', fontFamily: "'JetBrains Mono', monospace" }}>
            {wo.line} · {wo.operator || '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase' }}>Due</div>
          <div style={{ fontSize: 10,
            color: daysLeft !== null && daysLeft < 0 ? 'hsl(0,84%,60%)' :
                   daysLeft !== null && daysLeft <= 2 ? 'hsl(43,96%,56%)' : 'hsl(0,0%,65%)',
            fontFamily: "'JetBrains Mono', monospace" }}>
            {wo.planned_end ? new Date(wo.planned_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
            {daysLeft !== null && daysLeft <= 2 && daysLeft >= 0 && (
              <span style={{ marginLeft: 4, fontSize: 8, color: 'hsl(43,96%,56%)' }}>!!</span>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase' }}>Progress</span>
          <span style={{ fontSize: 9, color: cfg.color, fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700 }}>{progress}%</span>
        </div>
        <div style={{ height: 3, background: 'hsl(0,0%,12%)', position: 'relative' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: cfg.color, transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 6 }}>
        {wo.status === 'planned' && (
          <button data-testid={`start-wo-${wo.wo_no}`}
            onClick={() => onStatusChange(wo.id, 'in_progress')}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
              background: 'hsl(200,80%,20%)', border: '1px solid hsl(200,80%,35%)',
              color: 'hsl(200,80%,70%)', fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <Play size={10} weight="fill" /> Start
          </button>
        )}
        {wo.status === 'in_progress' && (
          <button data-testid={`complete-wo-${wo.wo_no}`}
            onClick={() => onStatusChange(wo.id, 'completed')}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
              background: 'hsl(197,100%,8%)', border: '1px solid hsl(197,100%,20%)',
              color: 'hsl(197,100%,48%)', fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <CheckCircle size={10} weight="fill" /> Complete
          </button>
        )}
        {wo.status === 'qc_hold' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
            background: 'hsl(12,90%,10%)', border: '1px solid hsl(12,90%,30%)',
            color: 'hsl(12,90%,55%)', fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <Warning size={10} /> Awaiting QC Release
          </span>
        )}
      </div>

      {/* Workflow Actions */}
      <div style={{ marginTop: 8 }}>
        <WorkflowActions recordType="work_order" record={wo} userRole={userRole} />
      </div>
    </div>
  );
}

export default function Factory() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fgToast, setFgToast] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [woForm, setWoForm] = useState({
    sales_order_id: '', order_no: '', product_name: '', product_sku: '',
    planned_qty: '', line: '', operator: '', planned_end: '',
  });

  const { user } = useAuth();
  const highlightId = useHighlight();
  const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } = useWorkflowDraft('factory', 'create_wo');
  const { setVisibleIds } = useAriaContext();

  // Apply prefill when it arrives (from action button cross-tab handoff)
  useEffect(() => {
    if (!prefill) return;
    setWoForm(prev => ({
      ...prev,
      sales_order_id: prefill.order_id || prev.sales_order_id,
      order_no: prefill.order_no || prev.order_no,
      product_name: prefill.product_name || prev.product_name,
      product_sku: prefill.product_sku || prev.product_sku,
      planned_qty: prefill.planned_qty || prev.planned_qty,
    }));
    setShowCreate(true);
  }, [prefill]);

  useEffect(() => { loadWOs(); }, []);
  useEffect(() => () => setVisibleIds([]), []); // eslint-disable-line

  const loadWOs = async () => {
    try { const r = await api.get('/work-orders'); setWorkOrders(r.data); setVisibleIds((r.data || []).slice(0, 20).map(w => w.wo_no)); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const wo = workOrders.find(w => w.id === id);
      const payload = { status };
      // Pass completed_qty when marking complete (default to planned_qty)
      if (status === 'completed' && wo) {
        payload.completed_qty = wo.completed_qty > 0 ? wo.completed_qty : wo.planned_qty;
      }
      const r = await api.put(`/work-orders/${id}/status`, payload);
      // Show FG stock toast if stock was updated
      if (status === 'completed' && r.data?.fg_stock_update) {
        const upd = r.data.fg_stock_update;
        const msg = upd.action === 'created'
          ? `New FG stock created: ${upd.item} (+${(upd.qty_added || 0).toLocaleString()} units)`
          : `FG stock updated: ${upd.item} +${(upd.qty_added || 0).toLocaleString()} units`;
        setFgToast(msg);
        setTimeout(() => setFgToast(null), 5000);
      }
      loadWOs();
    } catch (e) { console.error(e); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/work-orders', {
        ...woForm,
        planned_qty: parseInt(woForm.planned_qty) || 0,
      });
      clearDraft();
      setShowCreate(false);
      setWoForm({
        sales_order_id: '', order_no: '', product_name: '', product_sku: '',
        planned_qty: '', line: '', operator: '', planned_end: '',
      });
      loadWOs();
    } catch (e) { console.error(e); }
  };

  const columns = ['planned', 'in_progress', 'qc_hold', 'completed'];
  const byStatus = (s) => workOrders.filter(w => w.status === s);

  const stats = {
    total: workOrders.length,
    planned: workOrders.filter(w => w.status === 'planned').length,
    inProg: workOrders.filter(w => w.status === 'in_progress').length,
    hold: workOrders.filter(w => w.status === 'qc_hold').length,
  };

  return (
    <div data-testid="factory-page">
      {/* FG Stock Toast */}
      {fgToast && (
        <div data-testid="fg-stock-toast"
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999,
            padding: '10px 18px', background: 'hsl(142,65%,15%)',
            border: '1px solid hsl(142,60%,35%)',
            color: 'hsl(142,70%,65%)', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, letterSpacing: '0.05em', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle size={14} weight="fill" color="hsl(142,70%,55%)" />
          {fgToast}
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Factory</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)',
            textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Production Planning Board
          </div>
        </div>
        <button
          data-testid="create-wo-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
            background: 'hsl(75,100%,50%)', border: 'none', color: 'hsl(0,0%,3%)',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer' }}>
          <Plus size={12} weight="bold" /> New Work Order
        </button>
      </div>

      {/* Stats row */}
      <div className="metrics-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Work Orders', val: stats.total, color: 'hsl(0,0%,80%)' },
          { label: 'Planned', val: stats.planned, color: 'hsl(0,0%,60%)' },
          { label: 'In Progress', val: stats.inProg, color: 'hsl(200,80%,55%)' },
          { label: 'QC Hold', val: stats.hold, color: 'hsl(12,90%,55%)' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)',
            padding: '14px 16px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
              textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700,
              color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div style={{ color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Loading...</div>
      ) : (
        <div className="table-scroll" style={{ paddingBottom: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(240px, 1fr))', gap: 1, minWidth: 900 }}>
          {columns.map(col => {
            const cfg = STATUS_CONFIG[col];
            const items = byStatus(col);
            return (
              <div key={col} style={{ background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,12%)' }}>
                {/* Column header */}
                <div style={{ padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,12%)',
                  background: `${cfg.bg}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: cfg.color,
                    textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
                    {cfg.label}
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
                    color: cfg.color }}>{items.length}</span>
                </div>
                {/* Column cards */}
                <div style={{ padding: 8, minHeight: 200 }}>
                  {items.map(wo => (
                    <WOCard
                      key={wo.id}
                      wo={wo}
                      onStatusChange={handleStatusChange}
                      highlightId={highlightId}
                      userRole={user?.role}
                    />
                  ))}
                  {items.length === 0 && (
                    <div style={{ padding: 20, textAlign: 'center', color: 'hsl(0,0%,25%)',
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textTransform: 'uppercase',
                      letterSpacing: '0.1em' }}>
                      No orders
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Create Work Order Modal */}
      {showCreate && (
        <div data-testid="create-wo-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,15%)',
            width: '100%', maxWidth: 520, padding: 24, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, fontWeight: 700,
                color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>New Work Order</div>
              <button onClick={() => setShowCreate(false)}
                style={{ background: 'none', border: 'none', color: 'hsl(0,0%,50%)', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>

            <DraftRestoreBanner hasDraft={hasDraft} onRestore={restoreDraft} onDiscard={discardDraft} />

            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={LBL}>Sales Order</label>
                  <SmartPicker
                    endpoint="/orders"
                    params={{ status: 'confirmed' }}
                    labelKey="order_no"
                    searchKeys={['order_no', 'customer_name', 'product_name']}
                    placeholder="Search confirmed orders..."
                    value={woForm.order_no}
                    onSelect={(order) => {
                      setWoForm(prev => ({
                        ...prev,
                        sales_order_id: order.id,
                        order_no: order.order_no,
                        product_name: order.product_name || '',
                        product_sku: order.product_sku || '',
                        planned_qty: order.quantity || '',
                      }));
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={LBL}>Product Name</label>
                    <input style={INP} value={woForm.product_name}
                      onChange={e => setWoForm(prev => ({ ...prev, product_name: e.target.value }))}
                      placeholder="Product name" />
                  </div>
                  <div>
                    <label style={LBL}>Product SKU</label>
                    <input style={INP} value={woForm.product_sku}
                      onChange={e => setWoForm(prev => ({ ...prev, product_sku: e.target.value }))}
                      placeholder="SKU" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={LBL}>Planned Qty</label>
                    <input style={INP} type="number" value={woForm.planned_qty}
                      onChange={e => setWoForm(prev => ({ ...prev, planned_qty: e.target.value }))}
                      placeholder="0" required />
                  </div>
                  <div>
                    <label style={LBL}>Planned End Date</label>
                    <input style={INP} type="date" value={woForm.planned_end}
                      onChange={e => setWoForm(prev => ({ ...prev, planned_end: e.target.value }))} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={LBL}>Line</label>
                    <input style={INP} value={woForm.line}
                      onChange={e => setWoForm(prev => ({ ...prev, line: e.target.value }))}
                      placeholder="e.g. Line A" />
                  </div>
                  <div>
                    <label style={LBL}>Operator</label>
                    <input style={INP} value={woForm.operator}
                      onChange={e => setWoForm(prev => ({ ...prev, operator: e.target.value }))}
                      placeholder="Operator name" />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreate(false)}
                  style={{ padding: '8px 18px', background: 'transparent',
                    border: '1px solid hsl(0,0%,20%)', color: 'hsl(0,0%,50%)',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11, cursor: 'pointer',
                    textTransform: 'uppercase' }}>
                  Cancel
                </button>
                <button type="submit" data-testid="submit-wo-btn"
                  style={{ padding: '8px 18px', background: 'hsl(75,100%,50%)', border: 'none',
                    color: 'hsl(0,0%,3%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Create Work Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
